import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { DEMO_BUSINESS } from '../demoData';
import { isChannelVisible } from '../config/featureFlags';
import { formatDate } from '../utils/formatDate';

const THEME_COLOR_OPTIONS = ['#6C5CE7', '#0984E3', '#00B894', '#E17055', '#FD79A8', '#FDCB6E', '#636E72', '#2D3436'];

function normalizeThemeColor(color) {
  if (!color || typeof color !== 'string') return '#6C5CE7';
  const trimmed = color.trim();
  return /^#([0-9A-Fa-f]{6})$/.test(trimmed) ? trimmed.toUpperCase() : '#6C5CE7';
}

function businessToForm(business) {
  return {
    name: business.name,
    industry: business.industry,
    email: business.email,
    phone: business.phone,
    website: business.website || '',
    googleBusinessLink: business.googleBusinessLink || '',
    googleReviewLink: business.googleReviewLink || '',
    bookingUrl: business.bookingUrl || '',
    mailThemeColor: business.mailThemeColor,
  };
}

const INDUSTRY_OPTIONS = [
  'Salon', 'Spa', 'Barber Shop', 'Pet Grooming', 'Car Wash',
  'Dental Clinic', 'Fitness Studio', 'Pathology Lab', 'Restaurant',
  'Cafe', 'Clinic', 'Physiotherapy', 'Nail Salon', 'Others',
];

function SectionCard({ title, description, action, children }) {
  return (
    <div className="section">
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2>{title}</h2>
          {description && <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>{description}</p>}
        </div>
        {action}
      </div>
      <div className="card">{children}</div>
    </div>
  );
}

function ConfirmDialog({ open, title, message, onConfirm, onCancel, confirmLabel = 'Yes', cancelLabel = 'No' }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440, padding: 28 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>{title}</h2>
        <p style={{ margin: '0 0 24px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{message}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onCancel}>{cancelLabel}</button>
          <button className="btn btn-primary" onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, isDemo, setBusinessName } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    industry: '',
    email: '',
    phone: '',
    website: '',
    googleBusinessLink: '',
    googleReviewLink: '',
    bookingUrl: '',
    mailThemeColor: '#6C5CE7',
  });
  const initialFormRef = useRef(null);
  const [waStatus, setWaStatus] = useState({ connected: false, phoneNumber: null, connectedAt: null });
  const [waLoading, setWaLoading] = useState(false);
  const [businessCountry, setBusinessCountry] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [industryConfirm, setIndustryConfirm] = useState({ open: false, newIndustry: '' });

  const isDirty = initialFormRef.current !== null && JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  useEffect(() => {
    if (isDemo) {
      const initial = businessToForm({
        ...DEMO_BUSINESS,
        mailThemeColor: DEMO_BUSINESS.mailThemeColor,
      });
      setForm(initial);
      initialFormRef.current = initial;
      setLoading(false);
      return;
    }
    api
      .getBusiness()
      .then((d) => {
        const initial = businessToForm(d.business);
        setForm(initial);
        initialFormRef.current = initial;
        setBusinessCountry(d.business.country);
        if (d.business.whatsapp) {
          setWaStatus({
            connected: d.business.whatsapp.connected,
            phoneNumber: d.business.whatsapp.phoneNumber,
            connectedAt: d.business.whatsapp.connectedAt,
          });
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isDemo]);

  const isPro = user?.plan === 'pro' || user?.plan === 'custom';
  const channelAccess = user?.channelAccess || { email: true, sms: false, whatsapp: false };
  const currentPlan = user?.plan || 'free';

  const handleWhatsAppConnect = useCallback(() => {
    if (isDemo) {
      setWaStatus({ connected: true, phoneNumber: '+1 555 000 1234', connectedAt: new Date().toISOString() });
      toast.success('WhatsApp connected (demo)');
      return;
    }

    const configId = process.env.REACT_APP_META_CONFIG_ID;
    const appId = process.env.REACT_APP_META_APP_ID;
    if (!configId || !appId) {
      toast.error('WhatsApp integration is not configured yet. Please set up your Meta App credentials.');
      return;
    }

    setWaLoading(true);
    const FB = window.FB;
    if (!FB) {
      toast.error('Facebook SDK not loaded. Please refresh the page.');
      setWaLoading(false);
      return;
    }

    const timeout = setTimeout(() => {
      setWaLoading(false);
      toast.error('WhatsApp connection timed out. Please try again.');
    }, 60000);

    FB.login(
      (response) => {
        clearTimeout(timeout);
        if (response.authResponse) {
          const code = response.authResponse.code;
          const signupData = response.authResponse;
          api
            .connectWhatsApp({
              code,
              wabaId: signupData.waba_id || signupData.wabaId || '',
              phoneNumberId: signupData.phone_number_id || signupData.phoneNumberId || '',
            })
            .then((result) => {
              setWaStatus({ connected: true, phoneNumber: result.phoneNumber, connectedAt: result.connectedAt });
              toast.success('WhatsApp Business connected!');
            })
            .catch((err) => toast.error(err.message))
            .finally(() => setWaLoading(false));
        } else {
          toast.error('WhatsApp connection was cancelled.');
          setWaLoading(false);
        }
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: { solutionID: process.env.REACT_APP_META_SOLUTION_ID || '' } },
      }
    );
  }, [isDemo, toast]);

  const handleWhatsAppDisconnect = async () => {
    if (isDemo) {
      setWaStatus({ connected: false, phoneNumber: null, connectedAt: null });
      toast.success('WhatsApp disconnected (demo)');
      return;
    }

    setWaLoading(true);
    try {
      await api.disconnectWhatsApp();
      setWaStatus({ connected: false, phoneNumber: null, connectedAt: null });
      toast.success('WhatsApp disconnected.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setWaLoading(false);
    }
  };

  const handleIndustryChange = (newIndustry) => {
    if (newIndustry !== initialFormRef.current?.industry && initialFormRef.current?.industry) {
      setIndustryConfirm({ open: true, newIndustry });
    } else {
      setForm(prev => ({ ...prev, industry: newIndustry }));
    }
  };

  const handleIndustryConfirm = (updateTemplates) => {
    const newIndustry = industryConfirm.newIndustry;
    setForm(prev => ({ ...prev, industry: newIndustry, _updateTemplates: updateTemplates }));
    setIndustryConfirm({ open: false, newIndustry: '' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    const { _updateTemplates, ...payload } = form;

    const invalidUrl = ['website', 'googleBusinessLink', 'googleReviewLink', 'bookingUrl'].find((key) => {
      const val = (payload[key] || '').trim();
      if (!val) return false;
      try {
        const u = new URL(val);
        return u.protocol !== 'http:' && u.protocol !== 'https:';
      } catch {
        return true;
      }
    });
    if (invalidUrl) {
      const labels = {
        website: 'Website URL',
        googleBusinessLink: 'Google Business Link',
        googleReviewLink: 'Google Review Link',
        bookingUrl: 'Booking / Appointment URL',
      };
      toast.error(`${labels[invalidUrl]} must be a valid URL starting with http:// or https://`);
      setSaving(false);
      return;
    }

    if (isDemo) {
      setTimeout(() => {
        const saved = businessToForm(payload);
        initialFormRef.current = saved;
        setForm(saved);
        toast.success('Settings saved');
        setSaving(false);
      }, 400);
      return;
    }

    try {
      const result = await api.updateBusiness({ ...payload, updateTemplates: _updateTemplates || false });
      const saved = businessToForm(result.business);
      initialFormRef.current = saved;
      setForm(saved);
      setBusinessName(saved.name);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1>Settings</h1>
          <p>Manage your business profile and integrations</p>
        </div>

        <div className="section">
          <div className="section-header">
            <div>
              <h2>Account Information</h2>
              <div style={{ height: 13, width: 140, background: 'var(--bg)', borderRadius: 4, marginTop: 4, animation: 'pulse 1.5s ease-in-out infinite' }} />
            </div>
          </div>
          <div className="card">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="form-group">
                  <div style={{ height: 12, width: 80, background: 'var(--bg)', borderRadius: 4, marginBottom: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
                  <div style={{ height: 40, width: '100%', background: 'var(--bg)', borderRadius: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="section">
          <div className="section-header">
            <div>
              <h2>Business Profile</h2>
              <div style={{ height: 13, width: 200, background: 'var(--bg)', borderRadius: 4, marginTop: 4, animation: 'pulse 1.5s ease-in-out infinite' }} />
            </div>
          </div>
          <div className="card">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="form-group">
                  <div style={{ height: 12, width: 100, background: 'var(--bg)', borderRadius: 4, marginBottom: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
                  <div style={{ height: 40, width: '100%', background: 'var(--bg)', borderRadius: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
                </div>
              ))}
            </div>
            {[1, 2, 3].map(i => (
              <div key={i} className="form-group" style={{ marginTop: 16 }}>
                <div style={{ height: 12, width: 120, background: 'var(--bg)', borderRadius: 4, marginBottom: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
                <div style={{ height: 40, width: '100%', background: 'var(--bg)', borderRadius: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
              </div>
            ))}
            <div style={{ height: 40, width: 120, background: 'var(--bg)', borderRadius: 8, marginTop: 20, animation: 'pulse 1.5s ease-in-out infinite' }} />
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'general', label: 'Settings', icon: '⚙️' },
  ];

  if (isChannelVisible('whatsapp') && (isPro || channelAccess.whatsapp)) {
    tabs.push({ id: 'whatsapp', label: 'WhatsApp', icon: '📱' });
  }

  return (
    <div>
      <div className="page-header">
        <h1>Settings</h1>
        <p>Manage your account and business profile</p>
      </div>

      {user && !user.emailVerified && !isDemo && (
        <div className="alert alert-warning" style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Please verify your email address to unlock all features.</span>
          <button
            className="btn btn-sm"
            style={{ background: '#F59E0B', color: 'white', whiteSpace: 'nowrap' }}
            onClick={async () => {
              try {
                await api.resendVerification();
                toast.success('Verification email sent!');
              } catch (err) {
                toast.error(err.message);
              }
            }}
          >
            Resend Email
          </button>
        </div>
      )}

      {/* Tab Navigation - only show if multiple tabs */}
      {tabs.length > 1 && (
        <div style={{
          display: 'flex', gap: 4, background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 10, padding: 4, marginBottom: 24, overflowX: 'auto',
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 20px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s ease', whiteSpace: 'nowrap',
                display: 'flex', alignItems: 'center', gap: 8,
                background: activeTab === tab.id ? 'var(--primary)' : 'transparent',
                color: activeTab === tab.id ? 'white' : 'var(--text-secondary)',
              }}
            >
              <span style={{ fontSize: 15 }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Settings Tab - Account + Business Profile inline */}
      {activeTab === 'general' && (
        <>
          <SectionCard title="Account Information" description="Your account details">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label>Account Email</label>
                <input
                  value={user?.email || ''}
                  disabled
                  style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                />
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  Email address cannot be changed
                </p>
              </div>
              <div className="form-group">
                <label>Current Plan</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0' }}>
                  <span className={`badge ${currentPlan === 'free' ? 'badge-warning' : 'badge-purple'}`} style={{ textTransform: 'capitalize', fontSize: 13 }}>
                    {currentPlan} plan
                  </span>
                </div>
              </div>
              {businessCountry && (
                <div className="form-group">
                  <label>Country</label>
                  <input value={businessCountry} disabled style={{ background: '#f3f4f6', cursor: 'not-allowed' }} />
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    Country cannot be changed after setup
                  </p>
                </div>
              )}
              <div className="form-group">
                <label>Member since</label>
                <input
                  value={formatDate(user?.createdAt)}
                  disabled
                  style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Business Profile" description="Update your business information">
            <form onSubmit={handleSave}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>Business Name</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Industry</label>
                  <select
                    value={INDUSTRY_OPTIONS.includes(form.industry) ? form.industry : 'Others'}
                    onChange={(e) => handleIndustryChange(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 14, background: 'var(--surface)', color: 'var(--text)' }}
                  >
                    {INDUSTRY_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Business Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    required
                  />
                </div>
              <div className="form-group">
                <label>Mail Theme Color</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 10,
                    background: normalizeThemeColor(form.mailThemeColor),
                    border: '2px solid var(--border)', flexShrink: 0,
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', fontFamily: 'monospace' }}>
                      {normalizeThemeColor(form.mailThemeColor)}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      Current theme color
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  {THEME_COLOR_OPTIONS.filter(c => c !== normalizeThemeColor(form.mailThemeColor)).map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setForm({ ...form, mailThemeColor: color })}
                      aria-label={`Select theme color ${color}`}
                      style={{
                        width: 30, height: 30, borderRadius: '50%',
                        border: '2px solid var(--border)',
                        background: color, cursor: 'pointer', padding: 0,
                        transition: 'transform 0.15s ease',
                      }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    />
                  ))}
                  <label
                    style={{
                      width: 30, height: 30, borderRadius: '50%',
                      border: '2px dashed var(--border)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', position: 'relative', overflow: 'hidden',
                      fontSize: 14, color: 'var(--text-muted)',
                      transition: 'transform 0.15s ease',
                    }}
                    title="Pick custom color"
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    +
                    <input
                      type="color"
                      value={normalizeThemeColor(form.mailThemeColor).toLowerCase()}
                      onChange={(e) => setForm({ ...form, mailThemeColor: normalizeThemeColor(e.target.value) })}
                      style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                    />
                  </label>
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                  Used in email headers and buttons sent to your customers.
                </p>
              </div>
              <div className="form-group">
                <label>Website URL</label>
                  <input
                    type="url"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    placeholder="https://www.yourbusiness.com"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Google Business Link</label>
                <input
                  type="url"
                  value={form.googleBusinessLink}
                  onChange={(e) => setForm({ ...form, googleBusinessLink: e.target.value })}
                  placeholder="https://maps.google.com/?cid=... or https://g.page/yourbusiness"
                />
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  Your Google Maps / Business Profile link. If you don't add a review link below,
                  review requests will point customers here.
                </p>
              </div>
              <div className="form-group">
                <label>
                  Google Review Link{' '}
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span>
                </label>
                <input
                  type="url"
                  value={form.googleReviewLink}
                  onChange={(e) => setForm({ ...form, googleReviewLink: e.target.value })}
                  placeholder="https://search.google.com/local/writereview?placeid=..."
                />
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  A direct “write a review” link opens the review box instantly. Don't have one? Generate
                  it for free with your business name using{' '}
                  <a
                    href="https://productmate.com/google-review-link-generator"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    productmate.com/google-review-link-generator
                  </a>
                  , then paste it here. If left blank, we'll use your Google Business Link above.
                </p>
              </div>
              <div className="form-group">
                <label>Booking / Appointment URL</label>
                <input
                  type="url"
                  value={form.bookingUrl}
                  onChange={(e) => setForm({ ...form, bookingUrl: e.target.value })}
                  placeholder="https://calendly.com/yourbusiness"
                />
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  We'll include a trackable booking link in reminder messages.
                </p>
              </div>
              <button type="submit" className="btn btn-primary" disabled={saving || !isDirty}
                style={{ opacity: (saving || !isDirty) ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                {saving ? <span className="spinner" /> : 'Save Changes'}
              </button>
            </form>
          </SectionCard>
        </>
      )}

      {/* WhatsApp Tab */}
      {activeTab === 'whatsapp' && isChannelVisible('whatsapp') && (isPro || channelAccess.whatsapp) && (
        <SectionCard title="WhatsApp Integration" description="Connect your WhatsApp Business account">
          {waStatus.connected ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: 'white' }}>
                  ✓
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>Connected</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {waStatus.phoneNumber || 'WhatsApp Business'}
                    {waStatus.connectedAt && (
                      <> · Since {formatDate(waStatus.connectedAt)}</>
                    )}
                  </div>
                </div>
              </div>
              <button
                className="btn btn-danger btn-sm"
                onClick={handleWhatsAppDisconnect}
                disabled={waLoading}
              >
                {waLoading ? <span className="spinner" /> : 'Disconnect'}
              </button>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📱</div>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16, maxWidth: 400, margin: '0 auto 16px' }}>
                Connect your WhatsApp Business account to send review requests and reminders via WhatsApp.
              </p>
              <button
                className="btn"
                style={{ background: '#25D366', color: 'white', fontWeight: 600, padding: '12px 28px', fontSize: 15 }}
                onClick={handleWhatsAppConnect}
                disabled={waLoading}
              >
                {waLoading ? <span className="spinner" /> : 'Connect WhatsApp Business'}
              </button>
            </div>
          )}
        </SectionCard>
      )}

      {activeTab === 'whatsapp' && isChannelVisible('whatsapp') && !channelAccess.whatsapp && !isPro && currentPlan !== 'free' && (
        <SectionCard title="WhatsApp Integration">
          <div style={{ textAlign: 'center', padding: '32px 24px' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📱</div>
            <h3 style={{ margin: '0 0 8px', color: 'var(--text)' }}>WhatsApp Messaging</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: 14 }}>
              WhatsApp messaging is available on the Pro plan. Upgrade to connect your WhatsApp Business account.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/subscription')}
            >
              View Plans
            </button>
          </div>
        </SectionCard>
      )}

      <ConfirmDialog
        open={industryConfirm.open}
        title="Update Templates?"
        message="Do you want to update your review and reminder templates based on the new industry? Your existing custom templates will not be affected."
        confirmLabel="Yes"
        cancelLabel="No"
        onConfirm={() => handleIndustryConfirm(true)}
        onCancel={() => handleIndustryConfirm(false)}
      />
    </div>
  );
}
